import { BundleExtension } from "@prisma/client";

import { BUNDLE_EXTENSIONS } from "enums/bundleExtensions";
import { PAGE_TYPES } from "enums/pageTypes";
import { STORE_VERSIONS } from "enums/storeTypes";

const DEFAULT_EXTENSIONS = {
  [PAGE_TYPES.PHYSICAL]: {
    [STORE_VERSIONS.PHYSICAL]: [
      BUNDLE_EXTENSIONS.PRODUCT,
      BUNDLE_EXTENSIONS.ATTRIBUTES,
      BUNDLE_EXTENSIONS.MINT,
      BUNDLE_EXTENSIONS.HISTORY,
      BUNDLE_EXTENSIONS.DECENTRALAND,
      BUNDLE_EXTENSIONS.POAP,
      BUNDLE_EXTENSIONS.LINKS,
    ],
    [STORE_VERSIONS.UNIQLY]: [
      BUNDLE_EXTENSIONS.PRODUCT,
      BUNDLE_EXTENSIONS.ATTRIBUTES,
      BUNDLE_EXTENSIONS.MINT,
      BUNDLE_EXTENSIONS.HISTORY,
      BUNDLE_EXTENSIONS.DECENTRALAND,
      BUNDLE_EXTENSIONS.POAP,
      BUNDLE_EXTENSIONS.LINKS,
    ],
    [STORE_VERSIONS.PARTNER]: [
      BUNDLE_EXTENSIONS.PRODUCT,
      BUNDLE_EXTENSIONS.ATTRIBUTES,
      BUNDLE_EXTENSIONS.HISTORY,
      BUNDLE_EXTENSIONS.MINT,
      BUNDLE_EXTENSIONS.PARTNER_CONTACT,
      BUNDLE_EXTENSIONS.POAP,
      BUNDLE_EXTENSIONS.LINKS,
    ],
  },
  [PAGE_TYPES.VOUCHER]: {
    [STORE_VERSIONS.PHYSICAL]: [
      BUNDLE_EXTENSIONS.PRODUCT,
      BUNDLE_EXTENSIONS.ATTRIBUTES,
      BUNDLE_EXTENSIONS.HISTORY,
      BUNDLE_EXTENSIONS.REDEEM,
      BUNDLE_EXTENSIONS.SHIPPING_INFO,
    ],
    [STORE_VERSIONS.UNIQLY]: [
      BUNDLE_EXTENSIONS.PRODUCT,
      BUNDLE_EXTENSIONS.ATTRIBUTES,
      BUNDLE_EXTENSIONS.HISTORY,
      BUNDLE_EXTENSIONS.REDEEM,
      BUNDLE_EXTENSIONS.SHIPPING_INFO,
    ],
    [STORE_VERSIONS.PARTNER]: [
      BUNDLE_EXTENSIONS.PRODUCT,
      BUNDLE_EXTENSIONS.ATTRIBUTES,
      BUNDLE_EXTENSIONS.HISTORY,
      BUNDLE_EXTENSIONS.REDEEM,
      BUNDLE_EXTENSIONS.SHIPPING_INFO,
      BUNDLE_EXTENSIONS.PARTNER_CONTACT,
    ],
  },
};

class BundleExtensionService {
  private readonly extensionsList: BundleExtension[];
  private readonly storeType: STORE_VERSIONS;
  private readonly pageType: PAGE_TYPES;

  constructor({
    extensionsList,
    storeType,
    pageType,
  }: {
    extensionsList: BundleExtension[];
    storeType: STORE_VERSIONS;
    pageType: PAGE_TYPES;
  }) {
    this.extensionsList = extensionsList;
    this.storeType = storeType;
    this.pageType = pageType;
  }

  isActive(extensionName: BUNDLE_EXTENSIONS) {
    const extension = this.extensionsList.find(
      (extension) => extension.name === extensionName,
    );

    if (extension) {
      return extension.isActive;
    }

    const isDefaultExtension =
      DEFAULT_EXTENSIONS[this.pageType][this.storeType].includes(extensionName);

    if (isDefaultExtension) {
      return true;
    }

    return false;
  }

  getAllConfigsByName<T extends BUNDLE_EXTENSIONS>(
    extensionName: T,
  ): Extract<PrismaJson.BundleExtensionConfig, { extension: T }>[] {
    const extensions = this.extensionsList.filter(
      (extension) => extension.name === extensionName && extension.isActive,
    );

    const configs = extensions.map((extension) => extension.config);

    return configs as Extract<
      PrismaJson.BundleExtensionConfig,
      { extension: T }
    >[];
  }

  getConfig<T extends BUNDLE_EXTENSIONS>(
    extensionName: T,
  ): Extract<PrismaJson.BundleExtensionConfig, { extension: T }> | null {
    const extension = this.extensionsList.find(
      (extension) => extension.name === extensionName,
    );

    if (!extension) {
      return null;
    }

    return extension.config as Extract<
      PrismaJson.BundleExtensionConfig,
      { extension: T }
    >;
  }
}

export default BundleExtensionService;
